/**
 * Error code mappings for user-friendly error messages
 * Maps backend error codes (magic codes) to user-friendly messages
 *
 * @module errorCodes
 */

/**
 * Type representing an error code string
 */
export type ErrorCode = string;

/**
 * Mapping configuration for an error code
 */
export interface ErrorCodeMapping {
  /** User-friendly error message to display */
  message: string;
  /** Optional title for the error (defaults to 'Error') */
  title?: string;
}

/**
 * Maps error codes to user-friendly messages
 *
 * Add new mappings here as backend error codes are identified.
 * This ensures consistent, user-friendly error messages across the application.
 *
 * @example
 * ```ts
 * ERROR_CODE_MAPPINGS['NEW_ERROR_CODE'] = {
 *   message: 'User-friendly message',
 *   title: 'Error Title'
 * };
 * ```
 */
export const ERROR_CODE_MAPPINGS = {
  VALIDATION_ERROR: {
    message: 'Please review the highlighted fields and correct any errors.',
    title: 'Validation Error'
  },
  AUTH_NOT_AUTHORIZED: {
    message: 'You are not authorized to perform this action.',
    title: 'Authorization Error'
  },
  UNAUTHORIZED: {
    message: 'You are not authorized to perform this action.',
    title: 'Authorization Error'
  },
  NOT_FOUND: {
    message: 'The requested resource could not be found.',
    title: 'Not Found'
  },
  SERVER_ERROR: {
    message: 'An internal server error occurred. Please try again later.',
    title: 'Server Error'
  },
  NETWORK_ERROR: {
    message: 'Unable to connect to the server. Please check your internet connection.',
    title: 'Connection Error'
  },
  TIMEOUT: {
    message: 'The request took too long to complete. Please try again.',
    title: 'Request Timeout'
  },
  CONFLICT: {
    message: 'This action conflicts with the current state. Please refresh and try again.',
    title: 'Conflict'
  },
  FORBIDDEN: {
    message: 'You do not have permission to perform this action.',
    title: 'Access Denied'
  },
  BAD_REQUEST: {
    message: 'Invalid request. Please check your input and try again.',
    title: 'Invalid Request'
  },
  RATE_LIMIT: {
    message: 'Too many requests. Please wait a moment and try again.',
    title: 'Rate Limit Exceeded'
  }
} as const satisfies Record<ErrorCode, ErrorCodeMapping>;

/**
 * Gets a user-friendly error message for a given error code
 *
 * @param code - The error code to map (e.g., 'VALIDATION_ERROR', 'NOT_FOUND')
 * @returns The mapped error message configuration or undefined if not found
 *
 * @example
 * ```ts
 * const mapping = getErrorCodeMessage('VALIDATION_ERROR');
 * if (mapping) {
 *   showSnackbar(mapping.title, mapping.message, 'error');
 * }
 * ```
 */
export const getErrorCodeMessage = (code: ErrorCode): ErrorCodeMapping | undefined => {
  if (Object.hasOwn(ERROR_CODE_MAPPINGS, code)) {
    return ERROR_CODE_MAPPINGS[code as keyof typeof ERROR_CODE_MAPPINGS];
  }
  return undefined;
};

/**
 * Checks if an error code has a mapping in the error code mappings
 *
 * @param code - The error code to check
 * @returns True if a mapping exists, false otherwise
 *
 * @example
 * ```ts
 * if (hasErrorCodeMapping(errorCode)) {
 *   // Use mapped message
 * } else {
 *   // Use raw error message
 * }
 * ```
 */
export const hasErrorCodeMapping = (code: ErrorCode): boolean => {
  return Object.hasOwn(ERROR_CODE_MAPPINGS, code);
};
